import Image from "next/image"
import { ShieldAlert } from "lucide-react"

import { ApiRequestError } from "@/hooks/api/base"
import { useGetAuthUriMutation } from "@/hooks/api/email_integrations/mutations"
import { GenericDialog } from "@/components/dialogs"

const PROVIDER_CONFIG: Record<string, { src: string; alt: string; label: string; name: string }> = {
  google: { src: "/google.svg", alt: "Google", label: "Reconnect Google account", name: "Google" },
  microsoft: { src: "/microsoft.svg", alt: "Microsoft", label: "Reconnect Microsoft account", name: "Microsoft" },
  exchange: {
    src: "/exchange.svg",
    alt: "Exchange",
    label: "Reconnect Microsoft Exchange",
    name: "Microsoft Exchange",
  },
}

interface CalendarPermissionsDialogProps {
  error: Error | null
  onClose: () => void
}

export default function CalendarPermissionsDialog({ error, onClose }: CalendarPermissionsDialogProps) {
  const authMutation = useGetAuthUriMutation()
  const response =
    error instanceof ApiRequestError &&
    error.status === 403 &&
    typeof error.responseBody === "object" &&
    error.responseBody !== null
      ? error.responseBody
      : null
  const emailIntegrationId =
    response && "email_integration_id" in response && typeof response.email_integration_id === "string"
      ? response.email_integration_id
      : ""
  const provider =
    response && "provider" in response && typeof response.provider === "string" ? response.provider : "microsoft"
  const message = response && "error" in response && typeof response.error === "string" ? response.error : undefined
  const config = PROVIDER_CONFIG[provider] ?? PROVIDER_CONFIG.microsoft
  const body =
    message ??
    `Your ${config.name} integration needs additional permissions to modify events. Click below to reconnect.`

  function handleReconnect() {
    authMutation.mutate(
      { provider, emailIntegrationId, returnTo: window.location.pathname },
      {
        onSuccess: ({ auth_uri }) => {
          window.location.href = auth_uri
        },
      }
    )
  }

  return (
    <GenericDialog
      open={!!emailIntegrationId}
      onOpenChange={(open) => !open && onClose()}
      title="Calendar permissions required"
      logo={<Image src={config.src} alt={config.alt} width={24} height={24} />}
      action={{ label: config.label, onClick: handleReconnect, pending: authMutation.isPending }}
    >
      <div className="bg-yellow-background flex gap-2 rounded-md p-2">
        <ShieldAlert className="text-yellow-accent mt-0.5 size-5 shrink-0" />
        <p className="text-yellow-foreground">{body}</p>
      </div>
    </GenericDialog>
  )
}
